let workerPromise = null;
let activeWorkerParameterSignature = null;
let lastProgress = {
  status: 'idle',
  progress: 0
};

const DEFAULT_CASE_PREP_OCR_OUTPUT = Object.freeze({
  text: true,
  tsv: true,
  blocks: true
});

const BASE58_ALPHABET = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';
const BECH32_DATA_CHARSET_LOWER = 'qpzry9x8gf2tvdw0s3jn54khce6mua7l';
const BECH32_DATA_CHARSET_UPPER = BECH32_DATA_CHARSET_LOWER.toUpperCase();

const CASE_PREP_PSM = Object.freeze({
  SINGLE_BLOCK: '6',
  SINGLE_LINE: '7',
  SPARSE_TEXT: '11'
});

export const CASE_PREP_OCR_PROFILE_PARAMETERS = Object.freeze({
  default: Object.freeze({
    tessedit_pageseg_mode: CASE_PREP_PSM.SINGLE_BLOCK,
    tessedit_char_whitelist: '',
    preserve_interword_spaces: '1'
  }),
  cryptoLoose: Object.freeze({
    tessedit_pageseg_mode: CASE_PREP_PSM.SPARSE_TEXT,
    tessedit_char_whitelist: '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ',
    preserve_interword_spaces: '0'
  }),
  hexIdentifier: Object.freeze({
    tessedit_pageseg_mode: CASE_PREP_PSM.SPARSE_TEXT,
    tessedit_char_whitelist: '0123456789abcdefABCDEFxXoO',
    preserve_interword_spaces: '0'
  }),
  btcBase58: Object.freeze({
    tessedit_pageseg_mode: CASE_PREP_PSM.SPARSE_TEXT,
    tessedit_char_whitelist: BASE58_ALPHABET,
    preserve_interword_spaces: '0'
  }),
  btcBech32: Object.freeze({
    tessedit_pageseg_mode: CASE_PREP_PSM.SPARSE_TEXT,
    tessedit_char_whitelist: `1${BECH32_DATA_CHARSET_LOWER}${BECH32_DATA_CHARSET_UPPER}bBcCrRtT`,
    preserve_interword_spaces: '0'
  }),
  singleLineHexIdentifier: Object.freeze({
    tessedit_pageseg_mode: CASE_PREP_PSM.SINGLE_LINE,
    tessedit_char_whitelist: '0123456789abcdefABCDEFxXoO',
    preserve_interword_spaces: '0'
  }),
  singleLineBtcBase58: Object.freeze({
    tessedit_pageseg_mode: CASE_PREP_PSM.SINGLE_LINE,
    tessedit_char_whitelist: BASE58_ALPHABET,
    preserve_interword_spaces: '0'
  }),
  singleLineBtcBech32: Object.freeze({
    tessedit_pageseg_mode: CASE_PREP_PSM.SINGLE_LINE,
    tessedit_char_whitelist: `1${BECH32_DATA_CHARSET_LOWER}${BECH32_DATA_CHARSET_UPPER}bBcCrRtT`,
    preserve_interword_spaces: '0'
  })
});

function getBaseUrl() {
  const base = process.env.BASE_URL || '/';
  return String(base).endsWith('/') ? String(base).slice(0, -1) : String(base);
}

function makeReadableError(error, fallbackMessage) {
  if (error instanceof Error) {
    return error;
  }

  if (typeof error === 'string' && error.trim()) {
    return new Error(error);
  }

  if (error && typeof error.message === 'string' && error.message.trim()) {
    return new Error(error.message);
  }

  return new Error(fallbackMessage);
}

function isPlainObject(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function sanitizeWorkerParameters(parameters) {
  const sanitized = {};

  Object.keys(parameters || {})
    .sort()
    .forEach((key) => {
      const value = parameters[key];

      if (value === undefined || value === null) {
        return;
      }

      sanitized[key] = String(value);
    });

  return sanitized;
}

function getWorkerParameterSignature(parameters) {
  return Object.keys(parameters)
    .sort()
    .map((key) => `${key}:${parameters[key]}`)
    .join('|');
}

function getCasePrepOcrProfileParameters(profileName = 'default', parameterOverrides = null) {
  if (!Object.prototype.hasOwnProperty.call(CASE_PREP_OCR_PROFILE_PARAMETERS, profileName)) {
    throw new Error(`Unknown Case Prep OCR profile "${profileName}".`);
  }

  const profileParameters = CASE_PREP_OCR_PROFILE_PARAMETERS[profileName];

  return sanitizeWorkerParameters({
    ...CASE_PREP_OCR_PROFILE_PARAMETERS.default,
    ...profileParameters,
    ...(isPlainObject(parameterOverrides) ? parameterOverrides : {})
  });
}

function normalizeRectangle(rectangle) {
  if (!isPlainObject(rectangle)) {
    return null;
  }

  const left = Number(rectangle.left);
  const top = Number(rectangle.top);
  const width = Number(rectangle.width);
  const height = Number(rectangle.height);

  if (!Number.isFinite(left) || !Number.isFinite(top) || !Number.isFinite(width) || !Number.isFinite(height)) {
    throw new Error('OCR rectangle must contain finite left, top, width, and height values.');
  }

  if (width <= 0 || height <= 0) {
    throw new Error('OCR rectangle width and height must be greater than zero.');
  }

  return {
    left: Math.max(0, Math.round(left)),
    top: Math.max(0, Math.round(top)),
    width: Math.max(1, Math.round(width)),
    height: Math.max(1, Math.round(height))
  };
}

function resolveRecognizeInvocation(onProgressOrOptions, maybeOptions) {
  if (typeof onProgressOrOptions === 'function' || onProgressOrOptions == null) {
    return {
      onProgress: typeof onProgressOrOptions === 'function' ? onProgressOrOptions : undefined,
      options: isPlainObject(maybeOptions) ? maybeOptions : {}
    };
  }

  return {
    onProgress: typeof maybeOptions === 'function' ? maybeOptions : undefined,
    options: isPlainObject(onProgressOrOptions) ? onProgressOrOptions : {}
  };
}

function buildRecognizeConfig(options = {}) {
  const profile = typeof options.profile === 'string' && options.profile.trim()
    ? options.profile.trim()
    : 'default';

  const recognizeOptions = isPlainObject(options.recognizeOptions)
    ? { ...options.recognizeOptions }
    : {};

  const rectangle = normalizeRectangle(options.rectangle);
  if (rectangle) {
    recognizeOptions.rectangle = rectangle;
  }

  const output = {
    ...DEFAULT_CASE_PREP_OCR_OUTPUT,
    ...(isPlainObject(options.output) ? options.output : {})
  };

  return {
    profile,
    parameters: getCasePrepOcrProfileParameters(profile, options.parameterOverrides),
    recognizeOptions,
    output
  };
}

function dedupeStrings(values) {
  const seen = new Set();
  const result = [];

  (values || []).forEach((value) => {
    const normalized = typeof value === 'string' ? value.trim() : '';
    const key = normalized.toLowerCase();

    if (!normalized || seen.has(key)) {
      return;
    }

    seen.add(key);
    result.push(normalized);
  });

  return result;
}

function cleanTokenForCandidate(token) {
  return String(token || '')
    .trim()
    .replace(/^[`'"({[]+/, '')
    .replace(/[.,;:!?`'")}\]>]+$/g, '');
}
function buildCandidateKey(candidate) {
  return [
    candidate.kind || '',
    candidate.normalized || '',
    candidate.raw || ''
  ].join('|').toLowerCase();
}

function isHexString(value) {
  return /^[0-9a-fA-F]+$/.test(value);
}

function normalizeEvmLikeToken(rawToken) {
  const cleaned = cleanTokenForCandidate(rawToken);
  if (!cleaned) {
    return null;
  }

  const reasons = [];
  let working = cleaned;

  if (/^Ox/.test(working) || /^OX/.test(working) || /^ox/.test(working) || /^oX/.test(working)) {
    working = `0x${working.slice(2)}`;
    reasons.push('Normalized Ox/ox prefix to 0x.');
  }

  if (!/^0x/i.test(working)) {
    return null;
  }

  const body = working.slice(2);
  if (body.length !== 40 && body.length !== 64) {
    return null;
  }

  if (!/^[0-9a-fA-FoO]+$/.test(body)) {
    return null;
  }

  let repairedBody = body;
  if (/[oO]/.test(repairedBody)) {
    repairedBody = repairedBody.replace(/[oO]/g, '0');
    reasons.push('Converted OCR O/o characters to 0 inside a fixed-length hex token.');
  }

  if (!isHexString(repairedBody)) {
    return null;
  }

  const normalized = `0x${repairedBody}`;
  const kind = repairedBody.length === 40 ? 'evm-address' : 'tx-hash';
  const status = reasons.length > 0 ? 'safe_ocr_fix' : 'exact';
  const confidence = reasons.length > 0 ? 0.93 : 0.99;

  return {
    kind,
    raw: cleaned,
    normalized,
    status,
    confidence,
    reasons
  };
}

function normalizeBareHexHash(rawToken) {
  const cleaned = cleanTokenForCandidate(rawToken);
  if (!cleaned || cleaned.length !== 64 || !/^[0-9a-fA-FoO]+$/.test(cleaned)) {
    return null;
  }

  const reasons = [];
  let repaired = cleaned;

  if (/[oO]/.test(repaired)) {
    repaired = repaired.replace(/[oO]/g, '0');
    reasons.push('Converted OCR O/o characters to 0 inside a 64-character hex token.');
  }

  if (!isHexString(repaired)) {
    return null;
  }

  return {
    kind: 'tx-hash',
    raw: cleaned,
    normalized: repaired.toLowerCase(),
    status: reasons.length > 0 ? 'safe_ocr_fix' : 'exact',
    confidence: reasons.length > 0 ? 0.92 : 0.98,
    reasons
  };
}

function inspectBtcBase58LikeToken(rawToken) {
  const cleaned = cleanTokenForCandidate(rawToken);
  if (!/^[123mn2][A-Za-z0-9]{20,89}$/.test(cleaned)) {
    return null;
  }

  const reasons = [];
  if (/[0OIl]/.test(cleaned)) {
    reasons.push('Contains characters not allowed in Bitcoin Base58 addresses (0, O, I, or l).');
  }

  return {
    kind: 'btc-address',
    raw: cleaned,
    normalized: cleaned,
    status: reasons.length > 0 ? 'needs_review' : 'exact',
    confidence: reasons.length > 0 ? 0.55 : 0.8,
    reasons
  };
}

function inspectBtcBech32LikeToken(rawToken) {
  const cleaned = cleanTokenForCandidate(rawToken);
  if (!/^(bc1|BC1|tb1|TB1)[A-Za-z0-9]{8,90}$/.test(cleaned)) {
    return null;
  }

  const reasons = [];
  const hasLower = /[a-z]/.test(cleaned);
  const hasUpper = /[A-Z]/.test(cleaned);

  if (hasLower && hasUpper) {
    reasons.push('Bitcoin Bech32-style addresses should not use mixed case.');
  }

  const dataPart = cleaned.slice(3);
  if (/[bioBIO1]/.test(dataPart)) {
    reasons.push('Contains characters that are not valid in the Bech32 data portion.');
  }

  return {
    kind: 'btc-address',
    raw: cleaned,
    normalized: cleaned,
    status: reasons.length > 0 ? 'needs_review' : 'exact',
    confidence: reasons.length > 0 ? 0.55 : 0.8,
    reasons
  };
}

function analyzeCasePrepOcrCryptoText(text) {
  const sourceText = typeof text === 'string' ? text : '';
  const candidates = [];
  const seen = new Set();

  const regexes = [
    /(?:0x|0X|Ox|OX|ox|oX)[0-9a-fA-FoO]{40,64}/g,
    /\b[0-9a-fA-FoO]{64}\b/g,
    /\b[123mn2][A-Za-z0-9]{20,89}\b/g,
    /\b(?:bc1|BC1|tb1|TB1)[A-Za-z0-9]{8,90}\b/g
  ];

  regexes.forEach((regex) => {
    const matches = sourceText.match(regex) || [];

    matches.forEach((match) => {
      const candidate =
        normalizeEvmLikeToken(match)
        || normalizeBareHexHash(match)
        || inspectBtcBech32LikeToken(match)
        || inspectBtcBase58LikeToken(match);

      if (!candidate) {
        return;
      }

      const key = buildCandidateKey(candidate);
      if (seen.has(key)) {
        return;
      }

      seen.add(key);
      candidates.push(candidate);
    });
  });

  const addresses = dedupeStrings(
    candidates
      .filter((candidate) => candidate.kind === 'evm-address' || candidate.kind === 'btc-address')
      .map((candidate) => candidate.normalized)
  );

  const transactionHashes = dedupeStrings(
    candidates
      .filter((candidate) => candidate.kind === 'tx-hash')
      .map((candidate) => candidate.normalized)
  );

  const reviewCandidates = candidates
    .filter((candidate) => candidate.status !== 'exact')
    .map((candidate) => ({
      raw: candidate.raw,
      normalized: candidate.normalized,
      kind: candidate.kind,
      status: candidate.status,
      reasons: [...candidate.reasons]
    }));

  return {
    candidates,
    summary: {
      addresses,
      transactionHashes,
      reviewCandidates,
      counts: {
        total: candidates.length,
        addresses: addresses.length,
        transactionHashes: transactionHashes.length,
        reviewCandidates: reviewCandidates.length
      }
    }
  };
}

function attachCryptoCandidateMetadata(ocrData) {
  const safeData = ocrData && typeof ocrData === 'object' ? ocrData : {};
  const analysis = analyzeCasePrepOcrCryptoText(safeData.text || '');

  return {
    ...safeData,
    cryptoCandidates: analysis.candidates,
    cryptoCandidateSummary: analysis.summary
  };
}

async function ensureAssetReachable(url, label) {
  let response = null;

  try {
    response = await fetch(url, {
      method: 'GET',
      cache: 'no-cache',
      credentials: 'same-origin'
    });
  } catch (error) {
    throw new Error(`Could not reach OCR ${label} at ${url}.`);
  }

  if (!response || !response.ok) {
    throw new Error(`OCR ${label} was not reachable at ${url} (HTTP ${response ? response.status : 'unknown'}).`);
  }
}

async function applyCasePrepWorkerParameters(worker, parameters) {
  const signature = getWorkerParameterSignature(parameters);

  if (signature === activeWorkerParameterSignature) {
    return;
  }

  await worker.setParameters(parameters);
  activeWorkerParameterSignature = signature;
}

async function createCasePrepWorker(onProgress) {
  const tesseract = await import('tesseract.js');
  const { createWorker, OEM } = tesseract;

  const base = `${getBaseUrl()}/ocr`;

  await ensureAssetReachable(`${base}/worker.min.js`, 'worker file');
  await ensureAssetReachable(`${base}/core/tesseract-core.wasm.js`, 'core runtime');
  await ensureAssetReachable(`${base}/lang/eng.traineddata.gz`, 'language data');

  let worker = null;

  try {
    worker = await createWorker(
      'eng',
      OEM && typeof OEM.LSTM_ONLY !== 'undefined' ? OEM.LSTM_ONLY : 1,
      {
        workerBlobURL: false,
        workerPath: `${base}/worker.min.js`,
        corePath: `${base}/core`,
        langPath: `${base}/lang`,
        cachePath: 'case-prep-buddy-eng',
        cacheMethod: 'write',
        gzip: true,
        logger: (message) => {
          lastProgress = {
            status: message && message.status ? message.status : 'working',
            progress: message && typeof message.progress === 'number'
              ? message.progress
              : lastProgress.progress
          };

          if (typeof onProgress === 'function') {
            onProgress({ ...lastProgress });
          }
        },
        errorHandler: (error) => {
          console.error('[CasePrepBuddy OCR worker error]', error);
        }
      }
    );

    await applyCasePrepWorkerParameters(
      worker,
      getCasePrepOcrProfileParameters('default')
    );

    return worker;
  } catch (error) {
    if (worker) {
      try {
        await worker.terminate();
      } catch (terminateError) {
        console.warn('[CasePrepBuddy OCR] worker terminate after failed startup', terminateError);
      }
    }

    throw makeReadableError(
      error,
      'Could not start the local OCR reader.'
    );
  }
}

export function getCasePrepOcrProfileNames() {
  return Object.keys(CASE_PREP_OCR_PROFILE_PARAMETERS);
}

export function getCasePrepOcrProfileConfig(profileName = 'default', parameterOverrides = null) {
  return getCasePrepOcrProfileParameters(profileName, parameterOverrides);
}

export function extractCasePrepCryptoCandidatesFromText(text) {
  return analyzeCasePrepOcrCryptoText(text);
}

export async function warmupCasePrepOcr(onProgress) {
  if (!workerPromise) {
    workerPromise = createCasePrepWorker(onProgress).catch((error) => {
      workerPromise = null;
      activeWorkerParameterSignature = null;
      throw makeReadableError(error, 'Could not start the local OCR reader.');
    });
  }

  return workerPromise;
}

export async function recognizeCasePrepImage(imageInput, onProgressOrOptions, maybeOptions) {
  const { onProgress, options } = resolveRecognizeInvocation(onProgressOrOptions, maybeOptions);
  const worker = await warmupCasePrepOcr(onProgress);

  try {
    const { parameters, recognizeOptions, output } = buildRecognizeConfig(options);

    await applyCasePrepWorkerParameters(worker, parameters);

    const result = await worker.recognize(
      imageInput,
      recognizeOptions,
      output
    );

    return attachCryptoCandidateMetadata(result && result.data ? result.data : null);
  } catch (error) {
    throw makeReadableError(
      error,
      'Could not read the screenshot locally.'
    );
  }
}

export async function recognizeCasePrepImageRegion(imageInput, rectangle, onProgressOrOptions, maybeOptions) {
  const { onProgress, options } = resolveRecognizeInvocation(onProgressOrOptions, maybeOptions);

  return recognizeCasePrepImage(
    imageInput,
    onProgress,
    {
      ...options,
      rectangle
    }
  );
}

export async function recognizeCasePrepImageWithProfile(imageInput, profileName, onProgressOrOptions, maybeOptions) {
  const { onProgress, options } = resolveRecognizeInvocation(onProgressOrOptions, maybeOptions);

  return recognizeCasePrepImage(
    imageInput,
    onProgress,
    {
      ...options,
      profile: profileName
    }
  );
}

export function getCasePrepOcrProgress() {
  return { ...lastProgress };
}

export async function terminateCasePrepOcr() {
  if (!workerPromise) {
    return;
  }

  try {
    const worker = await workerPromise;
    await worker.terminate();
  } catch (error) {
    console.warn('[CasePrepBuddy OCR] terminate failed', error);
  } finally {
    workerPromise = null;
    activeWorkerParameterSignature = null;
    lastProgress = {
      status: 'idle',
      progress: 0
    };
  }
}